/**
 * Слой запросов к Passport API для survey-client (профиль, registration-survey).
 *
 * @module requests
 */

import {
  getPassportProfileUrl,
  getPassportRegistrationSurveyUrl,
  REGISTRATION_SURVEY_REQUEST_TIMEOUT_MS,
} from '../constants';

import { FetchError, sendRequest } from './sendRequest';
import type { ISendRequestResponse } from './sendRequest';

export type { ISendRequestResponse } from './sendRequest';
export { FetchError, sendRequest } from './sendRequest';

/** Тело ответа API статуса регистрационного опроса */
export interface RegistrationSurveyResponseBody {
  isNeedRunRegistrationSurvey: boolean;
}

/** Тело ответа API профиля (подмножество полей passport-client) */
export interface UserProfileResponseBody {
  id: string;
  email: string;
  emailVerified: boolean;
  username: string;
  locale: string;
  languages: string[];
  totp: boolean;
  socialLinks: unknown[];
  phone: string | null;
  phoneVerified: boolean;
  birthday?: string;
  country?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  contacts?: Record<string, unknown>;
  deleteRequestTimestamp?: number;
}

export interface FetchRegistrationSurveyOptions {
  token: string | null;
}

/**
 * GET статуса регистрационного опроса. При 404 возвращает `data: null` без исключения.
 * Таймаут 30 с (см. {@link REGISTRATION_SURVEY_REQUEST_TIMEOUT_MS}).
 *
 * @param {FetchRegistrationSurveyOptions} options — токен доступа
 * @returns {Promise<ISendRequestResponse<RegistrationSurveyResponseBody | null>>}
 */
export async function fetchRegistrationSurveyRequest(
  options: FetchRegistrationSurveyOptions,
): Promise<ISendRequestResponse<RegistrationSurveyResponseBody | null>> {
  const { token } = options;
  if (token === null || token === undefined || token.trim() === '') {
    throw new Error('fetchRegistrationSurveyRequest: token is required');
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const surveyUrl = getPassportRegistrationSurveyUrl();
  try {
    return await sendRequest<RegistrationSurveyResponseBody>({
      method: 'GET',
      url: surveyUrl,
      headers,
      timeoutMs: REGISTRATION_SURVEY_REQUEST_TIMEOUT_MS,
    });
  } catch (error) {
    if (error instanceof FetchError && error.status === 404) {
      return {
        data: null,
        ok: true,
        status: 404,
        statusText: 'Not Found',
        headers: error.headers,
        url: surveyUrl,
      };
    }
    throw error;
  }
}

/**
 * GET профиля пользователя.
 *
 * @param {string | null} token — access token
 * @returns {Promise<ISendRequestResponse<UserProfileResponseBody>>}
 */
export async function fetchUserProfileRequest(
  token: string | null,
): Promise<ISendRequestResponse<UserProfileResponseBody>> {
  if (token === null || token === undefined || token.trim() === '') {
    throw new Error('fetchUserProfileRequest: token is required');
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  return sendRequest<UserProfileResponseBody>({
    method: 'GET',
    url: getPassportProfileUrl(),
    headers,
  });
}
